"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Calendar, ShieldCheck, Activity, Cpu, CircleCheck as CheckCircle2, Sparkles, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const keyHighlights = [{
  id: 1,
  title: "Regulated Compliance",
  desc: "GMP/GLP ready systems"
}, {
  id: 2,
  title: "Application Support",
  desc: "Specialist workflow method design"
}, {
  id: 3,
  title: "Lifecycle Assurance",
  desc: "Validation, IQ/OQ and preventive care"
}];
export default function TechnologyHero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="technologyportfoliohero" data-nav="dark" className="relative overflow-hidden bg-sidebar text-sidebar-foreground py-20 lg:py-28">
      {/* Background Decorative Grid and Ambient Glows */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,hsl(207_55%_24%/0.75),transparent_60%),radial-gradient(ellipse_at_bottom_left,hsl(19_89%_54%/0.12),transparent_50%)]" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(207_50%_24%/0.2)_1px,transparent_1px),linear-gradient(to_bottom,hsl(207_50%_24%/0.2)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_80%)]" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column: Core Positioning Copy */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6 text-left">
            {/* Breadcrumb Navigation */}
            <motion.nav aria-label="Breadcrumb" initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.3
          }}>
              <ol className="flex flex-wrap items-center gap-1.5 text-xs sm:text-sm text-sidebar-foreground/70">
                <li className="inline-flex items-center gap-1.5">
                  <Link to="/HomePage" className="transition-colors hover:text-sidebar-foreground underline-offset-4 hover:underline">
                    Home
                  </Link>
                </li>
                <li aria-hidden="true" className="text-sidebar-foreground/50">
                  <ChevronRight className="size-3.5" />
                </li>
                <li className="inline-flex items-center gap-1.5">
                  <span className="font-medium text-sidebar-foreground" aria-current="page">
                    Products
                  </span>
                </li>
              </ol>
            </motion.nav>

            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }}>
              <Badge variant="secondary" className="bg-sidebar-accent text-sidebar-foreground border-sidebar-border px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-2">
                <Sparkles className="size-3.5 text-primary" />
                <span>Analytical Instrumentation & Process Systems</span>
              </Badge>
            </motion.div>

            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="space-y-4">
              <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-sidebar-foreground leading-[1.12]">
                Technology Portfolio Overview
              </h1>
              
              <p className="font-default text-base sm:text-lg text-sidebar-foreground/85 max-w-2xl leading-relaxed">
                Application-led analytical technology, qualification, and lifecycle support for regulated laboratories and manufacturing teams across Ireland and the UK. Explore proven platforms engineered for precision, throughput, and data integrity.
              </p>
            </motion.div>

            {/* Dual CTAs */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-medium px-6 shadow-md transition-all rounded-md">
                <Link to="#categories">
                  <span>Explore Technology Categories</span>
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-sidebar-border bg-sidebar-accent/60 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-foreground font-medium px-6 transition-all rounded-md">
                <Link to="#consultation">
                  <SafeHydrate><Calendar className="size-4 mr-2 text-primary" /></SafeHydrate>
                  <span>Schedule Consultation</span>
                </Link>
              </Button>
            </motion.div>

            {/* Key Value Micro-Points */}
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-sidebar-border/60 w-full">
              {keyHighlights.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col space-y-1">
                  <div className="flex items-center gap-1.5 font-medium text-xs sm:text-sm text-sidebar-foreground">
                    <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                    <span>{item.title}</span>
                  </div>
                  <p className="text-xs text-sidebar-foreground/70 pl-5">
                    {item.desc}
                  </p>
                </div>)}
            </motion.div>
          </div>

          {/* Right Column: Laboratory Instrument Showcase Visual */}
          <div className="lg:col-span-5 relative">
            <motion.div initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative mx-auto w-full max-w-lg lg:max-w-none">
              {/* Main Visual Glass Card */}
              <div className="relative rounded-2xl overflow-hidden border border-sidebar-border bg-sidebar-accent/40 backdrop-blur-md shadow-2xl p-3">
                <div className="relative aspect-4/3 w-full overflow-hidden rounded-xl bg-sidebar/80">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analytical_spectrometer_chromatography_system.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analytical_spectrometer_chromatography_system.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/laboratory_analytical_spectrometer_chromatography_system.webp-wvc-sizes" alt="High-precision laboratory analytical instrumentation and process system in a modern pharmaceutical laboratory" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                  <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-sidebar via-transparent to-transparent opacity-80" />

                  {/* Instrument Badge Overlay */}
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between gap-2 p-3 rounded-lg bg-sidebar/90 border border-sidebar-border backdrop-blur-sm">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 rounded-md bg-sidebar-accent text-primary">
                        <Activity className="size-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-sidebar-foreground">High-Throughput Analytics</p>
                        <p className="text-[11px] text-sidebar-foreground/70">Online Monitoring & Quality Verification</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-[11px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">
                      Live Setup
                    </span>
                  </div>
                </div>
              </div>

              {/* Floating Stat Card - Top Right */}
              <motion.div initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="absolute -top-4 -right-2 sm:-right-4 p-3.5 rounded-xl border border-sidebar-border bg-sidebar/95 backdrop-blur-md shadow-xl flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/15 text-primary">
                  <ShieldCheck className="size-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-sidebar-foreground">Audit Trail Support</div>
                  <div className="text-[11px] text-sidebar-foreground/70">Data Integrity & Logging</div>
                </div>
              </motion.div>

              {/* Floating Stat Card - Bottom Left */}
              <motion.div initial={shouldReduceMotion ? {
              opacity: 1
            } : {
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="hidden sm:flex absolute -bottom-4 -left-4 p-3.5 rounded-xl border border-sidebar-border bg-sidebar/95 backdrop-blur-md shadow-xl items-center gap-3">
                <div className="p-2 rounded-lg bg-sidebar-accent text-primary">
                  <Cpu className="size-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold text-sidebar-foreground">Full OEM Integration</div>
                  <div className="text-[11px] text-sidebar-foreground/70">Ireland & UK Support</div>
                </div>
              </motion.div>

            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}